import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ProposalFocusCard } from './cards/ProposalFocusCard';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import { getAgent } from '../../utils/agents';
import type { Proposal } from '../../types/db';
interface ProposalFocusProps {
  pending: Proposal[];
  outcomes: Record<string, 'ok' | 'nee'>;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onEdit: () => void;
  /** Vertical offset from the front card to Anna's core — cards come from her and return into her */
  fromY: number;
  compact: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;
/** Visible strip of each card waiting behind the front one */
const PEEK = 30;
const MAX_BEHIND = 2;

/**
 * Anna brings proposals forward one at a time. The front card is fully opaque; the ones behind show
 * only a thin strip with their title — never their body.
 */
export function ProposalFocus({
  pending,
  outcomes,
  onApprove,
  onReject,
  onEdit,
  fromY,
  compact
}: ProposalFocusProps) {
  const reduce = useMotionPreference();
  const [order, setOrder] = useState<string[]>([]);
  const ids = pending.map(p => p.id);
  const kept = order.filter(id => ids.includes(id));
  const ordered = [...kept, ...ids.filter(id => !kept.includes(id))].map(id => pending.find(p => p.id === id)).filter((p): p is Proposal => Boolean(p));
  const front = ordered[0];
  const behind = ordered.slice(1, 1 + MAX_BEHIND);
  const total = ordered.length;
  const later = () => setOrder([...ordered.slice(1), ordered[0]].map(p => p.id));
  return <section aria-label="Wacht op jou" className="relative w-full" style={{
    paddingTop: PEEK * behind.length
  }} data-id="element-1699" data-mp="86835ry0jp6m">
      {/* Peek strips: furthest back first, each a little narrower; only the title shows */}
      <AnimatePresence initial={false} data-id="element-1700" data-mp="22ixwo8kgywdh">
        {behind.map((p, i) => ({
        p,
        depth: i + 1
      })).reverse().map(({
        p,
        depth
      }) => {
        const a = getAgent(p.agent);
        const inset = depth * 22;
        return <motion.div key={`peek-${p.id}`} aria-hidden className="pointer-events-none absolute overflow-hidden rounded-t-[24px] border border-b-0 border-white/10 bg-night-850" style={{
          left: inset,
          right: inset,
          top: (behind.length - depth) * PEEK,
          height: PEEK + 28,
          zIndex: 5 - depth,
          filter: `brightness(${1 - depth * 0.12})`
        }} initial={{
          opacity: 0,
          y: reduce ? 0 : 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.15
          }
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} data-id="element-1701" data-mp="m74f5mi6ozq">
                <p className="flex items-center gap-2.5 truncate px-6 text-[18px] font-medium leading-[30px] text-ink-soft" data-id="element-1702" data-mp="2va2ly2gu7kyc">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{
              backgroundColor: a.color
            }} data-id="element-1703" data-mp="2msacgej61ny0" />
                  <span className="truncate" data-id="element-1704" data-mp="2iqouwfvoslhh">{p.title}</span>
                </p>
              </motion.div>;
      })}
      </AnimatePresence>

      <div className="relative z-10" data-id="element-1705" data-mp="2rsevocbgkwrx">
        <AnimatePresence custom={outcomes} mode="popLayout" initial data-id="element-1706" data-mp="2rtg7ij0atnap">
          {front && <motion.div key={front.id} custom={outcomes} className="relative" style={{
          transformOrigin: '50% 0%'
        }} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: fromY,
          scale: 0.25
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          transition: reduce ? {
            duration: 0.2
          } : {
            type: 'spring',
            stiffness: 280,
            damping: 30
          }
        }} variants={{
          exit: (o: Record<string, 'ok' | 'nee'>) => reduce ? {
            opacity: 0,
            transition: {
              duration: 0.2
            }
          } : o[front.id] === 'ok' ? {
            opacity: 0,
            y: fromY,
            scale: 0.15,
            transition: {
              duration: 0.3,
              ease: EASE
            }
          } : o[front.id] === 'nee' ? {
            opacity: 0,
            y: 80,
            transition: {
              duration: 0.22,
              ease: EASE
            }
          } : {
            opacity: 0,
            transition: {
              duration: 0.15
            }
          }
        }} exit="exit" data-id="element-1707" data-mp="2422gqhhspzru">
              <ProposalFocusCard proposal={front} position={1} total={total} compact={compact} onApprove={() => window.setTimeout(() => onApprove(front.id), 450)} onReject={() => onReject(front.id)} onEdit={onEdit} onLater={later} data-id="element-1708" data-mp="17b70zswj2d9b" />
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}